import { useParams, Link } from 'react-router-dom';
import { events, featuredEvent, formatReleaseSchedule, isEventPast } from './eventsData';
import { pastEvents } from './pastEventsData';
import PageHeader from './PageHeader';
import SnailOnly from './SnailOnly';
import EnquiryForm from './EnquiryForm';
import './EventPage.css';

export default function EventPage() {
  const { slug } = useParams();
  const ev = slug === featuredEvent.slug
    ? featuredEvent
    : events.find(e => e.slug === slug) || pastEvents.find(e => e.slug === slug);

  if (!ev) {
    return (
      <div className="event-page-not-found">
        <p>Event not found.</p>
        <Link to="/" className="btn btn-outline-dark">&larr; Back to home</Link>
      </div>
    );
  }

  const isFeatured = ev.slug === featuredEvent.slug;
  const isPast = pastEvents.some(e => e.slug === slug) || isEventPast(ev);
  const backHref = isPast ? "/past-events" : isFeatured ? "/#retreats" : "/#events";
  const backLabel = isPast ? "Past events" : isFeatured ? "Retreats" : "All events";

  return (
    <div className="event-page">
      <PageHeader />

      {/* Hero */}
      <div
        className={`event-page-hero${isFeatured ? ' event-page-hero--banner' : ''}`}
        style={{
          backgroundImage: `url(${ev.heroImg || ev.img})`,
          backgroundPosition: ev.heroPosition || 'center',
          height: ev.heroHeight,
        }}
      >
        <div className="event-page-hero-overlay">
          <Link to={backHref} className="event-page-back">
            &larr; {backLabel}
          </Link>
          {isFeatured && (
            <div className="event-page-hero-banner-text">
              <h1 className="event-page-hero-banner-title">Wild Alignment</h1>
              <p className="event-page-hero-banner-sub">Three days of movement, craft, and song</p>
              <p className="event-page-hero-banner-activities">Herbal skincare · Plant printing on fabric · Primal movement · Bush foods · Awareness practices</p>
            </div>
          )}
        </div>
        <SnailOnly className="snail-watermark" />
      </div>

      <div className="event-page-body">
        <div className="event-page-inner">

          {/* Title */}
          {ev.tag && <p className="section-label">{ev.tag}</p>}
          <h1 className="event-page-title">{ev.title}</h1>
          {isFeatured && (
            <p className="event-page-subtitle">
              <strong>Three days of movement, craft, and song</strong><br />
              Herbal skincare · Plant printing on fabric · Primal movement · Bush foods · Awareness practices
            </p>
          )}
          {isFeatured && ev.subtitle && (
            <p className="event-page-subtitle">{ev.subtitle}</p>
          )}

          {/* Key details panel */}
          <div className="event-details-panel">
            <div className="event-detail-item">
              <span className="event-detail-label">Date</span>
              <span className="event-detail-value">{ev.date}</span>
            </div>
            {ev.duration && (
              <div className="event-detail-item">
                <span className="event-detail-label">Duration</span>
                <span className="event-detail-value">{ev.duration}</span>
              </div>
            )}
            {ev.location && (
              <div className="event-detail-item">
                <span className="event-detail-label">Location</span>
                <span className="event-detail-value">{ev.location}</span>
              </div>
            )}
            {ev.format && (
              <div className="event-detail-item">
                <span className="event-detail-label">Format</span>
                <span className="event-detail-value">{ev.format}</span>
              </div>
            )}
            {ev.max && (
              <div className="event-detail-item">
                <span className="event-detail-label">Group size</span>
                <span className="event-detail-value">Max {ev.max} participants</span>
              </div>
            )}
            {isFeatured && ev.releases && (
              <div className="event-detail-item event-detail-item--full">
                <span className="event-detail-label">Price</span>
                <span className="event-detail-value event-detail-price">{formatReleaseSchedule(ev.releases)}</span>
              </div>
            )}
          </div>

          {/* Book button — early, above the fold */}
          <div className="event-page-cta">
            {ev.soldOut ? (
              <>
                <span className="btn event-card-book-btn event-card-book-btn--sold-out">Sold Out</span>
                <a href="#enquire" className="btn btn-outline-maroon event-page-book-btn">Enquire / Private Booking</a>
              </>
            ) : isPast ? (
              <>
                <span className="btn event-card-book-btn event-card-book-btn--sold-out">Event Passed</span>
                <a href="#enquire" className="btn btn-outline-maroon event-page-book-btn">Enquire / Private Booking</a>
              </>
            ) : ev.ticketUrl ? (
              <a href={ev.ticketUrl} className="btn btn-primary event-page-book-btn" target="_blank" rel="noopener noreferrer">
                Book Tickets &rarr;
              </a>
            ) : (
              <a href="#enquire" className="btn btn-primary event-page-book-btn">
                Enquire to Book &rarr;
              </a>
            )}
          </div>

          {/* Description */}
          <p className="event-page-desc">{ev.desc}</p>

          {/* Gallery */}
          {ev.images && ev.images.length > 0 && (
            <div className="event-page-gallery">
              {ev.images.map((src, i) => (
                <img key={i} src={src} alt="" className="event-page-gallery-img" />
              ))}
            </div>
          )}

          {/* Wild Alignment highlights */}
          {isFeatured && ev.highlights && (
            <div className="event-page-highlights">
              {ev.highlights.map(h => (
                <div key={h.heading} className="event-page-highlight">
                  <strong>{h.heading}{h.leader ? ` – ${h.leader}:` : ' –'}</strong> {h.body}
                </div>
              ))}
            </div>
          )}

          {isFeatured && ev.rhythm && (
            <p className="event-page-rhythm">{ev.rhythm}</p>
          )}

          {/* What to bring */}
          {ev.bring && (
            <div className="event-page-section">
              <h3 className="event-page-section-title">What to bring</h3>
              <p className="event-page-bring">{ev.bring}</p>
            </div>
          )}

          {/* Facilitators (Wild Alignment) */}
          {isFeatured && ev.closing && (
            <p className="event-page-closing">~~~~~ {ev.closing} ~~~~~</p>
          )}

          {isFeatured && ev.facilitators && (
            <div className="event-page-facilitators">
              <h2 className="event-page-facilitators-title">Your Facilitators</h2>
              {ev.facilitators.map((f, i) => (
                <div key={f.name} style={i > 0 ? { marginTop: '2.5rem' } : undefined}>
                  <div className="event-page-facilitator">
                    <h3 className="event-page-facilitator-name">{f.name}</h3>
                    {f.bio.map((para, j) => (
                      <p key={j}>{para}</p>
                    ))}
                    {f.website && (
                      <a href={f.website} target="_blank" rel="noopener noreferrer">{f.website}</a>
                    )}
                  </div>
                  {ev.facilitatorImage && i === 1 && (
                    <img src={ev.facilitatorImage} alt="" className="event-page-gallery-img" style={{ marginTop: '2.5rem', marginBottom: '2.5rem' }} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Embedded enquiry form */}
          <div className="event-page-section event-page-enquiry" id="enquire">
            <h3 className="event-page-section-title">{ev.soldOut || isPast || !ev.ticketUrl ? "Enquire to book" : "Private or group enquiry"}</h3>
            <p className="event-page-enquiry-sub">
              {ev.soldOut
                ? `This date is sold out, but we can arrange a private or group session of ${ev.title}. Tell us a bit about what you're after and we'll be in touch.`
                : isPast
                ? `This date has passed, but we can arrange a private or group session of ${ev.title}. Tell us a bit about what you're after and we'll be in touch.`
                : ev.slug === 'wild-alignment'
                ? "Interested in booking a private retreat for a group, or have a question before booking? Tell us a bit about what you're after and we'll be in touch."
                : `Interested in a private or group session of ${ev.title}, or have a question before booking? Tell us a bit about what you're after and we'll be in touch.`}
            </p>
            <EnquiryForm defaultSessions={ev.title} />
          </div>

          {/* Bottom actions */}
          <div className="event-page-actions">
            {ev.ticketUrl && !ev.soldOut && !isPast && (
              <a href={ev.ticketUrl} className="btn btn-primary event-page-book-btn" target="_blank" rel="noopener noreferrer">
                Book Tickets &rarr;
              </a>
            )}
            <Link to={backHref} className="btn btn-outline-dark">
              &larr; {backLabel}
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}
